import { EthersUtils } from './utils-index'
import { formatUnits } from 'ethers'

export class LSTHelper {
  private etherUtilsL1: EthersUtils
  private etherUtilsL2: EthersUtils
  private config: any

  constructor(config: any, metamask: { L1?: any; L2?: any }) {
    const { L1, L2 } = metamask
    if (L1) {
      this.etherUtilsL1 = new EthersUtils(L1)
    } else {
      this.etherUtilsL1 = new EthersUtils(config.L1.rpc, {
        privateKey: config.privateKey,
      })
    }
    if (L2) {
      this.etherUtilsL2 = new EthersUtils(L2)
    } else {
      this.etherUtilsL2 = new EthersUtils(config.L2.rpc, {
        privateKey: config.privateKey,
      })
    }

    this.config = config
  }
  async convertToShares(amount: string) {
    const vault = await this.etherUtilsL1.getContract(
      this.config.L1.contracts.deploy.Vault,
      this.config.abis.IVault
    )
    const shares = await vault.convertToShares(amount)
    return shares
  }
  async convertToAssets(amount: string) {
    const vault = await this.etherUtilsL1.getContract(
      this.config.L1.contracts.deploy.Vault,
      this.config.abis.IVault
    )
    const assets = await vault.convertToAssets(amount)
    return assets
  }

  async getUserUnstakeStatus(address: string, nonce: number) {
    const vault = await this.etherUtilsL1.getContract(
      this.config.L1.contracts.deploy.Vault,
      this.config.abis.IVault
    )
    const pendingUnstake = await vault.pendingUnstake(address, nonce)
    const pendingUnstakeAt = await vault.pendingUnstakeAt(address, nonce)
    const pendingUnstakeChain = await vault.pendingUnstakeChain(address, nonce)
    return {
      pendingUnstake,
      pendingUnstakeAt,
      pendingUnstakeChain,
    }
  }
  async getUserUnstakeFromNonce(address: string, fromNonce: number) {
    const vault = await this.etherUtilsL1.getContract(
      this.config.L1.contracts.deploy.Vault,
      this.config.abis.IVault
    )
    const totalNonce = await vault.nonces(address)
    const results: any[] = []

    for (let i = fromNonce; i < totalNonce; i++) {
      // 获取每个nonce的详细状态
      const unstakeStatus = await this.getUserUnstakeStatus(address, i)
      results.push({
        nonce: i,
        ...unstakeStatus,
      })
    }
    return results
  }

  async getVaultStatus() {
    try {
      const vault = await this.etherUtilsL1.getContract(
        this.config.L1.contracts.deploy.Vault,
        this.config.abis.IVault
      )

      const [totalStake, totalAssets] = await Promise.all([
        vault.totalStaked().catch(err => {
          console.log('获取总质押量失败:', err?.message || 'Unknown error')
          return 0n
        }),
        vault.totalAssets().catch(err => {
          console.log('获取总资产量失败:', err?.message || 'Unknown error')
          return 0n
        }),
      ])

      return {
        vaultTotalStake: totalStake,
        vaultTotalAssets: totalAssets,
      }
    } catch (err) {
      console.error('获取Vault状态失败:', err)
      return {
        vaultTotalStake: '0',
        vaultTotalAssets: '0',
      }
    }
  }

  async getUserL1Status(address: string) {
    const l1Name = this.config.L1.name

    try {
      // 创建 L1 上的合约实例
      const l1WstASTR = await this.etherUtilsL1.getContract(
        this.config.L1.contracts.WstASTR,
        this.config.abis.IERC20
      )
      const ASTRBalance = await this.etherUtilsL1.getBalance(address)

      // 获取余额数据
      const l1wstASTRBalance = await l1WstASTR.balanceOf(address).catch(err => {
        console.log('获取L1 WstASTR余额失败:', err?.message || 'Unknown error')
        return 0n
      })

      return {
        network: l1Name,
        balances: {
          ASTR: ASTRBalance,
          WstASTR: l1wstASTRBalance,
        },
      }
    } catch (err) {
      console.error('获取L1用户状态失败:', err)
      const error = err as Error
      return {
        network: l1Name,
        balances: {
          ASTR: '0',
          WstASTR: '0',
        },
        error: error?.message || 'Unknown error',
      }
    }
  }

  async getUserL2Status(address: string) {
    const l2Name = this.config.L2.name

    try {
      // 并行创建 L2 上的合约实例
      const [l2WstASTR, l2WASTR] = await Promise.all([
        this.etherUtilsL2.getContract(this.config.L2.contracts.WstASTR, this.config.abis.IERC20),
        this.etherUtilsL2.getContract(this.config.L2.contracts.WASTR, this.config.abis.IERC20),
      ])

      // 并行获取余额数据
      const [WstASTRBalance, wastrBalance] = await Promise.all([
        l2WstASTR.balanceOf(address).catch(err => {
          console.log('获取L2 WstASTR余额失败:', err?.message || 'Unknown error')
          return 0n
        }),
        l2WASTR.balanceOf(address).catch(err => {
          console.log('获取L2 WASTR余额失败:', err?.message || 'Unknown error')
          return 0n
        }),
      ])

      return {
        network: l2Name,
        balances: {
          WstASTR: WstASTRBalance,
          WASTR: wastrBalance,
        },
      }
    } catch (err) {
      console.error('获取L2用户状态失败:', err)
      const error = err as Error
      return {
        network: l2Name,
        balances: {
          WstASTR: '0',
          WASTR: '0',
        },
        error: error?.message || 'Unknown error',
      }
    }
  }

  // 如果需要获取完整状态，可以添加这个方便的方法
  async getUserStatus(address: string) {
    const [l1Status, l2Status] = await Promise.all([
      this.getUserL1Status(address),
      this.getUserL2Status(address),
    ])

    return {
      L1: l1Status,
      L2: l2Status,
    }
  }
  async getInstantUnstakeLimit() {
    const vault = await this.etherUtilsL1.getContract(
      this.config.L1.contracts.deploy.Vault,
      this.config.abis.IVault
    )
    const limit = await vault.getInstantUnstakeAmount()
    return limit
  }
  async getFees() {
    const vault = await this.etherUtilsL1.getContract(
      this.config.L1.contracts.deploy.Vault,
      this.config.abis.IVault
    )
    const withdrawFee = await vault.withdrawFee()
    const instantFee = await vault.instantFee()
    return {
      withdrawFee,
      instantFee,
    }
  }

  /**
   * 将 Substrate 地址转换为 hex bytes
   * @param substrateAddress Substrate 格式地址
   * @returns hex 格式的 bytes
   */
  private async substrateAddressToHexBytes(substrateAddress: string): Promise<string> {
    try {
      // 尝试从 @polkadot/util-crypto 导入（推荐）
      // 如果没有安装，可以从 @polkadot/keyring 导入
      let decodeAddress: (address: string) => Uint8Array
      
      try {
        const utilCrypto = await import('@polkadot/util-crypto')
        decodeAddress = utilCrypto.decodeAddress
      } catch {
        try {
          // 尝试使用 @polkadot/keyring（@polkadot/api 可能包含）
          const { Keyring } = await import('@polkadot/keyring')
          // Keyring 没有直接导出 decodeAddress，需要手动实现
          // 或者安装 @polkadot/util-crypto
          throw new Error(
            '请安装 @polkadot/util-crypto 进行地址转换：\n' +
            '运行: npm install @polkadot/util-crypto'
          )
        } catch {
          throw new Error(
            '需要安装 @polkadot/util-crypto 进行地址转换：\n' +
            '运行: npm install @polkadot/util-crypto'
          )
        }
      }

      const decoded = decodeAddress(substrateAddress)
      // 将 Uint8Array 转换为 hex 字符串
      return '0x' + Array.from(decoded).map(b => b.toString(16).padStart(2, '0')).join('')
    } catch (error) {
      console.error('地址转换失败:', error)
      throw error instanceof Error 
        ? error 
        : new Error(`无效的 Substrate 地址或地址转换失败: ${substrateAddress}`)
    }
  }

  /**
   * 读取指定质押者在特定合约上的质押金额
   * @param contract_id dApp 合约地址
   * @param staker_address 质押者地址（支持 EVM 或 Substrate 格式）
   * @returns 质押金额（bigint，单位为 wei）
   */
  async readStakedAmountOnContract(
    contract_id: string,
    staker_address: string,
  ): Promise<bigint> {
    try {
      // 判断地址格式并转换
      let staker_bytes: string
      if (staker_address.startsWith('0x') && staker_address.length === 42) {
        // EVM 地址：合约需要 bytes 类型，需要将地址转换为 bytes
        // 使用 ethers 的 getBytes 将地址转换为 bytes
        const addressBytes = this.etherUtilsL1.ethers.getBytes(staker_address)
        staker_bytes = this.etherUtilsL1.ethers.hexlify(addressBytes)
      } else {
        // Substrate 地址，转换为 hex bytes
        staker_bytes = await this.substrateAddressToHexBytes(staker_address)
      }

      // 获取 dApp Staking 合约地址
      // 根据 LSTMonitor 的实现，使用的是 dappStaking 合约
      const dappStakingAddress = this.config.L1.contracts.dappStaking
      
      // 创建包含 read_staked_amount_on_contract 的最小 ABI
      // 这个方法在 dApp Staking V3 接口中
      const dappStakingABI = [
        {
          inputs: [
            {
              internalType: 'address',
              name: 'contract_id',
              type: 'address',
            },
            {
              internalType: 'bytes',
              name: 'staker',
              type: 'bytes',
            },
          ],
          name: 'read_staked_amount_on_contract',
          outputs: [
            {
              internalType: 'uint128',
              name: 'amount',
              type: 'uint128',
            },
          ],
          stateMutability: 'view',
          type: 'function',
        },
      ]

      // 创建合约实例并调用
      const dappStakingContract = await this.etherUtilsL1.getContract(
        dappStakingAddress,
        dappStakingABI
      )

      // 调用 read_staked_amount_on_contract
      const result = await dappStakingContract.read_staked_amount_on_contract(
        contract_id,
        staker_bytes
      )

      return BigInt(result.toString())
    } catch (error) {
      console.error('读取质押金额失败:', error)
      throw error
    }
  }

  /**
   * 读取合约上最近的总质押金额
   * @param contract_id dApp 合约地址
   * @returns 总质押金额（bigint，单位为 wei）
   */
  async readContractStake(contract_id: string): Promise<bigint> {
    try {
      const dappStakingAddress = this.config.L1.contracts.dappStaking
      
      // 创建包含 read_contract_stake 的最小 ABI
      const dappStakingABI = [
        {
          inputs: [
            {
              internalType: 'address',
              name: 'contract_id',
              type: 'address',
            },
          ],
          name: 'read_contract_stake',
          outputs: [
            {
              internalType: 'uint128',
              name: 'total',
              type: 'uint128',
            },
          ],
          stateMutability: 'view',
          type: 'function',
        },
      ]

      const dappStakingContract = await this.etherUtilsL1.getContract(
        dappStakingAddress,
        dappStakingABI
      )

      const result = await dappStakingContract.read_contract_stake(contract_id)
      return BigInt(result.toString())
    } catch (error) {
      console.error('读取合约总质押金额失败:', error)
      throw error
    }
  }

  /**
   * 检查CCIP跨链转账是否部署成功
   * @param params
   * @returns
   */
  async checkVaultStatus() {
    try {
      // 使用L1的工具类和配置
      const utils = this.etherUtilsL1
      const config = this.config.L1

      // 获取Vault合约实例
      const vaultContract = await utils.getContract(
        config.contracts.deploy.Vault,
        this.config.abis.IVault
      )

      // 获取总质押量和总资产
      const totalStaked = await vaultContract.totalStaked()
      const totalAssets = await vaultContract.totalAssets()

      return {
        success: true,
        data: {
          totalStaked: totalStaked,
          totalAssets: totalAssets,
        },
        errors: [],
      }
    } catch (error) {
      console.error('获取Vault状态失败:', error)
      return {
        success: false,
        data: null,
        errors: [error instanceof Error ? error.message : String(error)],
      }
    }
  }
  /**
   * 获取CCIP跨链转账费用
   * @param params
   * @returns
   */
  async getCCIPFee(params: {
    fromChain: 'L1' | 'L2'
    toChain: 'L1' | 'L2'
    tokenAddress: string
    amount: string
  }) {
    const { fromChain, toChain, tokenAddress, amount } = params

    const sourceUtils = fromChain === 'L1' ? this.etherUtilsL1 : this.etherUtilsL2
    const sourceConfig = this.config[fromChain]
    const destConfig = this.config[toChain]

    // 构建CCIP消息
    const abiCoder = new sourceUtils.ethers.AbiCoder()
    const ccipMessage = {
      receiver: abiCoder.encode(['address'], [sourceUtils.ethers.ZeroAddress]), // 使用零地址作为占位符
      data: '0x',
      tokenAmounts: [
        {
          token: tokenAddress,
          amount: sourceUtils.ethers.parseEther(amount),
        },
      ],
      feeToken: sourceUtils.ethers.ZeroAddress, // 默认使用原生代币
      extraArgs: '0x',
    }

    try {
      // 检查链支持并获取费用
      const routerContract = await sourceUtils.getContract(
        sourceConfig.ccip.Router,
        this.config.abis.ICCIP_Router
      )

      // 检查链是否支持
      const isSupported = await routerContract.isChainSupported(destConfig.ccip.ChainSelector)
      if (!isSupported) {
        throw new Error(`Chain ${toChain} not supported`)
      }

      // 获取费用
      const fees = await routerContract.getFee(destConfig.ccip.ChainSelector, ccipMessage)

      return {
        feeWei: fees,
        feeFormatted: sourceUtils.ethers.formatEther(fees),
      }
    } catch (error) {
      console.error('获取CCIP费用失败:', error)
      throw error
    }
  }
  async ccipTransferCheck(
    params: {
      fromChain: 'L1' | 'L2'
      toChain: 'L1' | 'L2'
      message: any
    },
    address: string
  ) {
    const { fromChain, toChain, message } = params
    const sourceUtils = fromChain === 'L1' ? this.etherUtilsL1 : this.etherUtilsL2
    const sourceConfig = this.config[fromChain]
    const destConfig = this.config[toChain]
    const account = address
    let errors: any[] = []
    try {
      let isSuccess = true
      const checks = {
        chainSupport: false,
        fees: '0',
        feeToken: '',
        feeTokenBalance: '0',
        isFeeTokenSufficient: false,
        feeNeeded: '0',
        tokens: [] as {
          token: string
          amount: string
          balance: string
          allowance: string
          isAllowanceSufficient: boolean
          isBalanceSufficient: boolean
        }[],
        nativeBalance: '0',
        linkBalance: '0',
        linkAllowance: '0',
        isLinkAllowanceSufficient: false,
      }

      // 首先检查原生代币余额
      const nativeBalance = await sourceUtils.getBalance(account)
      checks.nativeBalance = sourceUtils.ethers.formatEther(nativeBalance)

      // 如果原生代币余额为0，将整体检查状态设为失败
      if (BigInt(nativeBalance) === BigInt(0)) {
        isSuccess = false
      }

      // 1. 检查链是否支持
      const routerContract = await sourceUtils.getContract(
        sourceConfig.ccip.Router,
        this.config.abis.ICCIP_Router
      )
      checks.chainSupport = await routerContract.isChainSupported(destConfig.ccip.ChainSelector)
      if (!checks.chainSupport) isSuccess = false

      // 2. 获取费用
      const fees = await routerContract.getFee(destConfig.ccip.ChainSelector, message)
      checks.fees = sourceUtils.ethers.formatEther(fees)

      // 修改费用代币检查逻辑
      const useNative = message.feeToken === sourceUtils.ethers.ZeroAddress
      checks.feeToken = useNative ? 'NATIVE' : 'LINK'

      if (useNative) {
        const nativeBalance = await sourceUtils.getBalance(account)
        checks.nativeBalance = sourceUtils.ethers.formatEther(nativeBalance)
        checks.feeTokenBalance = checks.nativeBalance
        checks.isFeeTokenSufficient = Number(nativeBalance) >= Number(fees)
        if (!checks.isFeeTokenSufficient) {
          isSuccess = false
          checks.feeNeeded = (Number(nativeBalance) - Number(fees)).toString()
          errors.push(
            `Insufficient ETH (min ${(Math.ceil(Number(formatUnits(fees, 18)) * 1000000) / 1000000).toFixed(6)} ETH) for CCIP fee.`
          )
        }
      } else {
        const linkContract = await sourceUtils.getContract(
          sourceConfig.ccip.LINK,
          this.config.abis.IERC20
        )
        const linkBalance = await linkContract.balanceOf(account)
        const linkAllowance = await linkContract.allowance(account, sourceConfig.ccip.Router)

        checks.linkBalance = sourceUtils.ethers.formatEther(linkBalance)
        checks.linkAllowance = sourceUtils.ethers.formatEther(linkAllowance)
        checks.feeTokenBalance = checks.linkBalance
        checks.isFeeTokenSufficient = BigInt(linkBalance) >= BigInt(fees)

        checks.isLinkAllowanceSufficient = BigInt(linkAllowance) >= BigInt(fees)

        // 如果LINK授权不足，将整体检查状态设为失败
        if (!checks.isLinkAllowanceSufficient) {
          isSuccess = false
          errors.push('LINK CCIP bridge fee is not enough!')
        }
      }

      // 3. 检查代币余额和授权（如果有代币转账）
      if (message.tokenAmounts && message.tokenAmounts.length > 0) {
        for (const tokenAmount of message.tokenAmounts) {
          const tokenContract = await sourceUtils.getContract(
            tokenAmount.token,
            this.config.abis.IERC20
          )

          const balance = await tokenContract.balanceOf(account)
          const allowance = await tokenContract.allowance(account, sourceConfig.ccip.Router)

          const isBalanceSufficient = balance >= tokenAmount.amount
          const isAllowanceSufficient = allowance >= tokenAmount.amount

          if (!isBalanceSufficient || !isAllowanceSufficient) {
            isSuccess = false
            errors.push('Token balance or allowance is not enough!')
          }

          checks.tokens.push({
            token: tokenAmount.token,
            amount: sourceUtils.ethers.formatEther(tokenAmount.amount),
            balance: sourceUtils.ethers.formatEther(balance),
            allowance: sourceUtils.ethers.formatEther(allowance),
            isAllowanceSufficient,
            isBalanceSufficient,
          })
        }
      }

      return {
        success: isSuccess,
        data: checks,
        errors: errors,
      }
    } catch (error) {
      console.error('CCIP转账检查失败:', error)
      return {
        success: false,
        data: null,
        errors: [error instanceof Error ? error.message : String(error)],
      }
    }
  }
  async approveMaxToRouter(chain: 'L1' | 'L2' = 'L2') {
    try {
      const results: any[] = []
      const utils = chain === 'L1' ? this.etherUtilsL1 : this.etherUtilsL2
      const maxUint256 = utils.ethers.MaxUint256
      const threshold = maxUint256 / 2n
      const account = await utils.getAccounts()
      const router = this.config[chain].ccip.Router

      // 根据链选择需要检查和授权的代币配置
      const tokenConfigs = {
        WstASTR: this.config[chain].contracts.WstASTR,
        link: this.config[chain].ccip.LINK,
        wASTR: this.config[chain].contracts.WASTR,
      }

      // 检查并授权每个代币
      for (const [name, tokenAddress] of Object.entries(tokenConfigs)) {
        const tokenContract = await utils.getContract(tokenAddress, this.config.abis.IERC20)
        const currentAllowance = await tokenContract.allowance(account, router)

        if (currentAllowance <= threshold) {
          console.log(`正在授权 ${chain} ${name}...`)

          const calldata = await utils.encodeDataByABI({
            abi: this.config.abis.IERC20,
            functionName: 'approve',
            executeArgs: [router, maxUint256],
            target: tokenAddress,
          })

          const result = await this.sendTransaction({
            chain: chain,
            calldata: calldata,
          })
          results.push(result)

          if (!result.success) {
            console.error(`${chain} ${name} 授权失败`)
            return {
              success: false,
              data: results,
              errors: [`${chain} ${name} 授权失败`],
            }
          }
        } else {
          console.log(`${chain} ${name} 已有足够授权`)
        }
      }

      return {
        success: true,
        data: results,
        errors: [],
      }
    } catch (error) {
      console.error(`${chain}授权代币失败:`, error)
      return {
        success: false,
        data: null,
        errors: [error instanceof Error ? error.message : String(error)],
      }
    }
  }
  async createBridgewstASTRMessage(params: {
    fromChain: 'L1' | 'L2'
    toChain: 'L1' | 'L2'
    amount: string | bigint
    receiverAddress: string
    useNative?: boolean
  }) {
    const { fromChain, toChain, amount, receiverAddress, useNative = false } = params

    const sourceUtils = fromChain === 'L1' ? this.etherUtilsL1 : this.etherUtilsL2
    const sourceConfig = this.config[fromChain]

    // 构建CCIP消息
    const abiCoder = new sourceUtils.ethers.AbiCoder()
    const message = {
      receiver: abiCoder.encode(['address'], [receiverAddress]),
      data: '0x',
      tokenAmounts: [
        {
          token: sourceConfig.contracts.WstASTR,
          amount: amount,
        },
      ],
      feeToken: useNative ? sourceUtils.ethers.ZeroAddress : sourceConfig.ccip.LINK,
      extraArgs: '0x',
    }

    return {
      fromChain,
      toChain,
      message,
    }
  }

  async getccipTransferCalldata(params: {
    fromChain: 'L1' | 'L2'
    toChain: 'L1' | 'L2'
    message: any
  }) {
    const { fromChain, toChain, message } = params
    const sourceUtils = fromChain === 'L1' ? this.etherUtilsL1 : this.etherUtilsL2
    const sourceConfig = this.config[fromChain]
    const destConfig = this.config[toChain]

    try {
      // 获取费用
      const routerContract = await sourceUtils.getContract(
        sourceConfig.ccip.Router,
        this.config.abis.ICCIP_Router
      )
      const fees = await routerContract.getFee(destConfig.ccip.ChainSelector, message)

      const useNative = message.feeToken === sourceUtils.ethers.ZeroAddress

      // 使用 encodeDataByABI 编码调用数据
      const calldata = await sourceUtils.encodeDataByABI({
        abi: this.config.abis.ICCIP_Router,
        functionName: 'ccipSend',
        executeArgs: [destConfig.ccip.ChainSelector, message],
        target: sourceConfig.ccip.Router,
        value: useNative ? fees.toString() : '0',
      })

      return { chain: fromChain, calldata: calldata }
    } catch (error) {
      console.error('获取CCIP调用数据失败:', error)
      throw error
    }
  }
  async sendTransaction(params: {
    chain: 'L1' | 'L2'
    calldata: {
      target: string
      data: string
      abi: any
      functionName: string
      executeArgs: any[]
      value?: string
    }
  }) {
    if (params.chain === 'L1') {
      return await this.etherUtilsL1.sendTransaction(params.calldata)
    } else {
      return await this.etherUtilsL2.sendTransaction(params.calldata)
    }
  }

  async ccipTransfer(params: { fromChain: 'L1' | 'L2'; toChain: 'L1' | 'L2'; message: any }) {
    const { fromChain, toChain, message } = params
    const sourceUtils = fromChain === 'L1' ? this.etherUtilsL1 : this.etherUtilsL2
    const sourceConfig = this.config[fromChain]
    const destConfig = this.config[toChain]

    try {
      const routerContract = await sourceUtils.getContractWithSigner(
        sourceConfig.ccip.Router,
        this.config.abis.ICCIP_Router
      )

      // 获取费用
      const fees = await routerContract.getFee(destConfig.ccip.ChainSelector, message)

      const useNative = message.feeToken === sourceUtils.ethers.ZeroAddress

      // 发送CCIP消息
      console.log('发送CCIP消息...')
      const tx = await routerContract.ccipSend(
        destConfig.ccip.ChainSelector,
        message,
        useNative ? { value: fees } : {}
      )

      console.log('等待交易确认...')
      const receipt = await tx.wait()

      // 从日志中获取messageId
      let messageId = ''
      for (const log of receipt.logs) {
        try {
          if (
            log.topics[0] ===
            sourceUtils.ethers.keccak256('MessageSent(bytes32,address,uint64,address,bytes)')
          ) {
            messageId = log.topics[1]
            break
          }
        } catch (e) {
          continue
        }
      }

      return {
        txHash: receipt.hash,
        messageId,
      }
    } catch (error) {
      console.error('详细错误信息:', error)
      throw error
    }
  }

  async ccipTransferToken(params: {
    fromChain: 'L1' | 'L2'
    toChain: 'L1' | 'L2'
    tokenAddress: string
    amount: string
    receiverAddress: string
    useNative?: boolean
  }) {
    const { fromChain, toChain, tokenAddress, amount, receiverAddress, useNative = false } = params

    const sourceUtils = fromChain === 'L1' ? this.etherUtilsL1 : this.etherUtilsL2
    const sourceConfig = this.config[fromChain]
    const destConfig = this.config[toChain]

    // 构建CCIP消息
    const abiCoder = new sourceUtils.ethers.AbiCoder()
    const message = {
      receiver: abiCoder.encode(['address'], [receiverAddress]),
      data: '0x',
      tokenAmounts: [
        {
          token: tokenAddress,
          amount: sourceUtils.ethers.parseEther(amount),
        },
      ],
      feeToken: useNative ? sourceUtils.ethers.ZeroAddress : sourceConfig.ccip.LINK,
      extraArgs: '0x',
    }
    const account = (await sourceUtils.getAccounts()) || ''

    try {
      // 检查代币余额
      const tokenContract = await sourceUtils.getContractWithSigner(
        tokenAddress,
        this.config.abis.IERC20
      )

      const balance = await tokenContract.balanceOf(account)
      const amountWei = sourceUtils.ethers.parseEther(amount)

      if (balance < amountWei) {
        throw new Error(
          `代币余额不足: 需要 ${amount}, 实际 ${sourceUtils.ethers.formatEther(balance)}`
        )
      }

      // 检查链支持并获取费用
      const routerContract = await sourceUtils.getContractWithSigner(
        sourceConfig.ccip.Router,
        this.config.abis.ICCIP_Router
      )

      console.log('检查链支持状态...')
      const isSupported = await routerContract.isChainSupported(destConfig.ccip.ChainSelector)
      if (!isSupported) {
        throw new Error(`Chain ${toChain} not supported`)
      }

      console.log('计算跨链费用...')
      const fees = await routerContract.getFee(destConfig.ccip.ChainSelector, message)
      const feeFormatted = sourceUtils.ethers.formatEther(fees)
      console.log('预估跨链费用:', sourceUtils.ethers.formatEther(fees), 'ETH')

      // 检查原生代币余额（如果使用原生代币支付费用）
      if (useNative) {
        const nativeBalance = await sourceUtils.getBalance(account)
        if (nativeBalance < fees) {
          throw new Error(
            `原生代币余额不足支付费用: 需要 ${sourceUtils.ethers.formatEther(
              fees
            )}, 实际 ${sourceUtils.ethers.formatEther(nativeBalance)}`
          )
        }
      }

      // 处理代币授权
      console.log('检查代币授权...')
      const currentAllowance = await tokenContract.allowance(account, sourceConfig.ccip.Router)

      if (currentAllowance < amountWei) {
        console.log('正在授权代币...')
        const approvalTx = await tokenContract.approve(sourceConfig.ccip.Router, amountWei)
        await approvalTx.wait()
        console.log('代币授权完成')
      } else {
        console.log('代币已有足够授权')
      }

      if (!useNative) {
        console.log('检查LINK授权...')
        const linkContract = await sourceUtils.getContractWithSigner(
          sourceConfig.ccip.LINK,
          this.config.abis.IERC20
        )

        const linkAllowance = await linkContract.allowance(account, sourceConfig.ccip.Router)

        if (linkAllowance < fees) {
          console.log('正在授权LINK...')
          const linkApprovalTx = await linkContract.approve(sourceConfig.ccip.Router, fees)
          await linkApprovalTx.wait()
          console.log('LINK授权完成')
        } else {
          console.log('LINK已有足够授权')
        }
      }

      // 发送CCIP消息
      console.log('发送CCIP消息...')
      console.log('参数:', {
        destChainSelector: destConfig.ccip.ChainSelector,
        message,
        value: useNative ? feeFormatted : undefined,
      })

      const tx = await routerContract.ccipSend(
        destConfig.ccip.ChainSelector,
        message,
        useNative ? { value: fees } : {}
      )

      console.log('等待交易确认...')
      const receipt = await tx.wait()

      // 从日志中获取messageId
      let messageId = ''
      for (const log of receipt.logs) {
        try {
          if (
            log.topics[0] ===
            sourceUtils.ethers.keccak256('MessageSent(bytes32,address,uint64,address,bytes)')
          ) {
            messageId = log.topics[1]
            break
          }
        } catch (e) {
          continue
        }
      }

      return {
        txHash: receipt.hash,
        messageId,
      }
    } catch (error) {
      console.error('详细错误信息:', error)
      throw error
    }
  }

  /**
   * Vault存款
   */
  async createVaultDepositMessage(params: {
    amount: string | bigint
    address: string
    useNative?: boolean
  }) {
    return this.createVaultMessage({
      type: 'VAULT_DEPOSIT',
      amount: params.amount,
      address: params.address,
      useNative: params.useNative,
    })
  }

  /**
   * Vault提现
   */
  async createVaultWithdrawMessage(params: {
    address: string
    useNative?: boolean
    nonce?: string | bigint | number
  }) {
    const abiCoder = new this.etherUtilsL2.ethers.AbiCoder()
    const messageData = abiCoder.encode(['uint256'], [params.nonce])
    return this.createVaultMessage({
      type: 'VAULT_WITHDRAW',
      address: params.address,
      useNative: params.useNative,
      messageData,
    })
  }

  /**
   * Vault请求解押
   */
  async createVaultUnstakeMessage(params: {
    amount: string | bigint
    address: string
    useNative?: boolean
  }) {
    return this.createVaultMessage({
      type: 'VAULT_REQUEST_UNSTAKE',
      amount: params.amount,
      address: params.address,
      useNative: params.useNative,
    })
  }

  async createVaultInstantUnstakeMessage(params: {
    amount: string | bigint
    address: string
    useNative?: boolean
  }) {
    return this.createVaultMessage({
      type: 'VAULT_INSTANT_WITHDRAW',
      amount: params.amount,
      address: params.address,
      useNative: params.useNative,
    })
  }

  /**
   * 内部方法：创建Vault相关的CCIP消息
   */
  private async createVaultMessage(params: {
    type: 'VAULT_DEPOSIT' | 'VAULT_WITHDRAW' | 'VAULT_REQUEST_UNSTAKE' | 'VAULT_INSTANT_WITHDRAW'
    amount?: string | bigint
    address: string
    useNative?: boolean
    messageData?: string
  }) {
    const { useNative = true, address } = params
    const sourceUtils = this.etherUtilsL2
    const sourceConfig = this.config.L2
    const destConfig = this.config.L1

    // 更新消息类型映射以包含 VAULT_INSTANT_WITHDRAW
    const messageTypeMap = {
      VAULT_DEPOSIT: 3,
      VAULT_WITHDRAW: 4,
      VAULT_REQUEST_UNSTAKE: 5,
      VAULT_INSTANT_WITHDRAW: 6,
    }

    const abiCoder = new sourceUtils.ethers.AbiCoder()

    let businessMessage
    if (params.messageData) {
      // 简化消息编码逻辑，只编码消息类型和空bytes
      businessMessage = abiCoder.encode(
        ['uint8', 'bytes'],
        [messageTypeMap[params.type], params.messageData]
      )
    } else {
      // 简化消息编码逻辑，只编码消息类型和空bytes
      businessMessage = abiCoder.encode(['uint8', 'bytes'], [messageTypeMap[params.type], '0x'])
    }
    // 生成一个基于时间戳和随机数的nonce，避免重复
    const nonce = BigInt(Date.now()) * 1000n + BigInt(Math.floor(Math.random() * 1000))

    const ccipData = abiCoder.encode(
      ['bytes', 'uint256', 'address'],
      [businessMessage, nonce, address]
    )

    // 编码extraArgs以指定更高的gas limit
    const extraArgs = this.createExtraArgs(1200000)

    // 修改 tokenAmounts 的构建逻辑
    let tokenAmounts: { token: string; amount: string | bigint }[] = []

    // 更新条件以包含 VAULT_INSTANT_WITHDRAW
    if (params.amount) {
      if (params.type === 'VAULT_DEPOSIT') {
        tokenAmounts = [
          {
            token: sourceConfig.contracts.WASTR,
            amount: params.amount,
          },
        ]
      } else if (
        params.type === 'VAULT_REQUEST_UNSTAKE' ||
        params.type === 'VAULT_INSTANT_WITHDRAW'
      ) {
        tokenAmounts = [
          {
            token: sourceConfig.contracts.WstASTR,
            amount: params.amount,
          },
        ]
      }
    }

    const message = {
      receiver: abiCoder.encode(['address'], [destConfig.contracts.deploy.CCIPBridge]),
      data: ccipData,
      tokenAmounts: tokenAmounts, // 使用新的 tokenAmounts
      feeToken: useNative ? sourceUtils.ethers.ZeroAddress : sourceConfig.ccip.LINK,
      extraArgs: extraArgs,
    }

    return {
      fromChain: 'L2' as 'L1' | 'L2',
      toChain: 'L1' as 'L1' | 'L2',
      message,
    }
  }
  async checkL1CCIPBalances(address?: string) {
    try {
      const utils = this.etherUtilsL1
      const config = this.config.L1
      const account = address || config.contracts.deploy.CCIPBridge

      // 需要检查的代币合约
      const contracts = {
        WASTR: await utils.getContractWithSigner(config.contracts.WASTR, this.config.abis.IERC20),
        LINK: await utils.getContractWithSigner(config.ccip.LINK, this.config.abis.IERC20),
      }

      // 获取各代币余额
      const balances = {
        native: await this.etherUtilsL1.getBalance(account),
        tokens: {} as Record<string, string>,
      }

      for (const [name, contract] of Object.entries(contracts)) {
        const balance = await contract.balanceOf(account)
        balances.tokens[name] = utils.ethers.formatEther(balance)
      }

      return {
        success: true,
        data: balances,
        errors: [],
      }
    } catch (error) {
      console.error('获取L1 CCIP地址余额失败:', error)
      return {
        success: false,
        data: null,
        errors: [error instanceof Error ? error.message : String(error)],
      }
    }
  }

  private createExtraArgs(gasLimit: number): string {
    const abiCoder = new this.etherUtilsL2.ethers.AbiCoder()

    // EVMExtraArgsV1 结构体编码
    // bytes4(keccak256("CCIP EVMExtraArgsV1")) = 0x97a657c9
    const EVM_EXTRA_ARGS_V1_TAG = '0x97a657c9'

    // 编码 EVMExtraArgsV1 结构体
    const encodedArgs = abiCoder.encode(
      ['tuple(uint256)'], // EVMExtraArgsV1 结构体
      [[BigInt(gasLimit)]] // 只需要 gasLimit 参数
    )

    // 组合 selector 和编码后的参数
    return EVM_EXTRA_ARGS_V1_TAG + encodedArgs.slice(2) // 去掉 encodedArgs 的 0x 前缀
  }
}
